import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useInvoices } from '../hooks/useInvoices';
import {
  ArrowLeft,
  Download,
  Mail,
  CreditCard,
  Calendar,
  FileText,
  User,
  DollarSign,
} from 'lucide-react';
import {
  supabase,
  getStatusColor,
  NumberUtils,
  DateUtils,
  LoadingState,
  ErrorState,
} from '@cleanpro/common-ui';

/**
 * InvoiceDetails component displays detailed information about a specific invoice.
 *
 * Features:
 * - Displays invoice header with number, status, and customer info
 * - Shows line items with descriptions, quantities, and amounts
 * - Displays payment details including subtotal, tax, discounts, and total
 * - Shows important dates (issue date, due date, paid date)
 * - Provides actions for downloading, sending, and marking as paid
 * - Handles loading and error states
 *
 * @component
 * @returns {JSX.Element} The rendered invoice details page
 */
export default function InvoiceDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getInvoiceById, loading, error, refreshInvoices } = useInvoices();
  const [sendingInvoice, setSendingInvoice] = useState(false);

  const invoice = id ? getInvoiceById(id) : undefined;

  /**
   * Send invoice notification with payment link
   */
  const handleSendInvoice = async () => {
    if (!invoice?.id) return;

    setSendingInvoice(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        'send-customer-invoice-notification',
        {
          body: { invoiceId: invoice.id },
        }
      );

      if (error) {
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to send invoice');
      }

      alert('Invoice sent successfully!');
      
      // Refresh invoice data to get updated status
      await refreshInvoices();
    } catch (err) {
      console.error('Error sending invoice:', err);
      alert(
        err instanceof Error ? err.message : 'Failed to send invoice'
      );
    } finally {
      setSendingInvoice(false);
    }
  };

  useEffect(() => {
    if (!loading && !invoice && id) {
      console.error('Invoice not found:', id);
    }
  }, [loading, invoice, id]);

  if (loading) {
    return <LoadingState message="Loading invoice details..." />;
  }

  if (error) {
    return <ErrorState error={error} />;
  }

  if (!invoice) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <FileText className="h-16 w-16 text-gray-400 mb-4" />
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">
          Invoice Not Found
        </h2>
        <p className="text-gray-500 mb-6">
          The invoice you're looking for doesn't exist or has been deleted.
        </p>
        <button
          onClick={() => navigate('/invoices')}
          className="flex items-center space-x-2 text-green-600 hover:text-green-700"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Invoices</span>
        </button>
      </div>
    );
  }

  const lineItems = invoice.lineItems || [];
  const subtotal = invoice.subtotal || invoice.amount || 0;
  const taxAmount = invoice.taxAmount || 0;
  const discountAmount = invoice.discountAmount || 0;
  const total = invoice.totalAmount || invoice.amount || 0;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/invoices')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Invoices</span>
        </button>
        
        <div className="flex items-center space-x-3">
          <button className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
            <Download className="h-4 w-4" />
            <span>Download PDF</span>
          </button>
          <button
            onClick={handleSendInvoice}
            disabled={sendingInvoice || invoice.status === 'sent' || invoice.status === 'paid'}
            className={`flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg transition-colors ${
              sendingInvoice || invoice.status === 'sent' || invoice.status === 'paid'
                ? 'opacity-50 cursor-not-allowed bg-gray-100'
                : 'hover:bg-gray-50'
            }`}
          >
            <Mail className="h-4 w-4" />
            <span>{sendingInvoice ? 'Sending...' : 'Send Invoice'}</span>
          </button>
          {invoice.status !== 'paid' && (
            <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
              <CreditCard className="h-4 w-4" />
              <span>Mark as Paid</span>
            </button>
          )}
        </div>
      </div>

      {/* Invoice Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Invoice Header */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 p-8 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {invoice.invoiceNumber || `INV-${invoice.id.slice(0, 8)}`}
              </h1>
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                  invoice.status,
                  'invoice'
                )}`}
              >
                {invoice.status}
              </span>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600 mb-1">Total Amount</div>
              <div className="text-3xl font-bold text-gray-900">
                {NumberUtils.formatCurrency(total)}
              </div>
            </div>
          </div>
        </div>

        {/* Customer & Date Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8 border-b border-gray-200">
          <div>
            <div className="flex items-center space-x-2 text-gray-600 mb-2">
              <User className="h-4 w-4" />
              <span className="text-sm font-medium">Bill To</span>
            </div>
            <div className="text-lg font-semibold text-gray-900">
              {invoice.customerName || 'Unknown Customer'}
            </div>
            {invoice.customerEmail && (
              <div className="text-sm text-gray-600 mt-1">
                {invoice.customerEmail}
              </div>
            )}
            {invoice.customerPhone && (
              <div className="text-sm text-gray-600 mt-1">
                {invoice.customerPhone}
              </div>
            )}
            {invoice.customerAddress && (
              <div className="text-sm text-gray-600 mt-2">
                {invoice.customerAddress}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-gray-600">
                <Calendar className="h-4 w-4" />
                <span className="text-sm">Issue Date:</span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {DateUtils.formatDate(invoice.issueDate)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-gray-600">
                <Calendar className="h-4 w-4" />
                <span className="text-sm">Due Date:</span>
              </div>
              <span className="text-sm font-medium text-gray-900">
                {DateUtils.formatDate(invoice.dueDate)}
              </span>
            </div>
            {invoice.paidAt && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-gray-600">
                  <CreditCard className="h-4 w-4" />
                  <span className="text-sm">Paid On:</span>
                </div>
                <span className="text-sm font-medium text-green-600">
                  {DateUtils.formatDate(invoice.paidAt)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Line Items */}
        <div className="p-8">
          <div className="flex items-center space-x-2 text-gray-600 mb-4">
            <FileText className="h-4 w-4" />
            <span className="text-sm font-medium">Line Items</span>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    Description
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">
                    Quantity
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">
                    Unit Price
                  </th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {lineItems.map((item, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="py-4 px-4 text-sm text-gray-900">
                      {item.description}
                    </td>
                    <td className="py-4 px-4 text-sm text-right text-gray-900">
                      {item.quantity}
                    </td>
                    <td className="py-4 px-4 text-sm text-right text-gray-900">
                      {NumberUtils.formatCurrency(item.unitPrice || 0)}
                    </td>
                    <td className="py-4 px-4 text-sm text-right font-medium text-gray-900">
                      {NumberUtils.formatCurrency(item.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-8 flex justify-end">
            <div className="w-full max-w-sm space-y-2">
              <div className="flex justify-between py-2">
                <span className="text-sm text-gray-600">Subtotal:</span>
                <span className="text-sm font-medium text-gray-900">
                  {NumberUtils.formatCurrency(subtotal)}
                </span>
              </div>
              
              {discountAmount > 0 && (
                <div className="flex justify-between py-2">
                  <span className="text-sm text-gray-600">Discount:</span>
                  <span className="text-sm font-medium text-green-600">
                    -{NumberUtils.formatCurrency(discountAmount)}
                  </span>
                </div>
              )}
              
              {taxAmount > 0 && (
                <div className="flex justify-between py-2">
                  <span className="text-sm text-gray-600">
                    Tax {invoice.taxRate ? `(${invoice.taxRate}%)` : ''}:
                  </span>
                  <span className="text-sm font-medium text-gray-900">
                    {NumberUtils.formatCurrency(taxAmount)}
                  </span>
                </div>
              )}
              
              <div className="flex justify-between py-3 border-t-2 border-gray-200">
                <span className="text-base font-semibold text-gray-900 flex items-center space-x-2">
                  <DollarSign className="h-5 w-5" />
                  <span>Total:</span>
                </span>
                <span className="text-xl font-bold text-gray-900">
                  {NumberUtils.formatCurrency(total)}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="text-sm font-medium text-gray-600 mb-2">Notes:</div>
              <div className="text-sm text-gray-900 whitespace-pre-wrap">
                {invoice.notes}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
